import { BLOCKS } from '@contentful/rich-text-types';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Text,
  Grid,
  GridItem,
  Hide,
  Show,
  Box,
} from '@everlywell/ui-kit';
import RichTextWrapper from 'components/RichTextWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { notEmpty } from 'utils/types/utilityTypes';

import SectionContainer from '../components/SectionContainer';

export type DigitalResultsSectionProps = {
  data: Queries.pdpSectionFragment;
};

/**
 * This section is used to render the Digital Results section on the product details page.
 */
const DigitalResultsSection = (props: DigitalResultsSectionProps) => {
  const section = props.data;

  if (!section) return null;
  const accordion = section.subsections?.[0];
  const accordionSections =
    accordion && 'subsections' in accordion ? accordion.subsections ?? [] : [];

  const trackAccordionClick = (title: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_ACCORDION,
      data: {
        accordionTitle: title || 'No title provided',
        contentfulSection: 'DigitalResultsSection',
      },
    });
  };

  return (
    <SectionContainer size={['sm', 'sm', 'xl']} overflow="clip">
      <Grid
        spacing={[10, 10, 20]}
        flexDirection={['column-reverse', null, 'row']}
      >
        <GridItem width={[1, null, 0.5]} display="flex" justifyContent="center">
          {/* render desktop image if it exists */}
          <Box position="sticky">
            {section.images?.[0]?.desktopImage?.gatsbyImageData && (
              <Show above="tablet">
                <GatsbyImage
                  image={section.images[0].desktopImage.gatsbyImageData}
                  alt={section.images[0].desktopImageAltText ?? ''}
                />
              </Show>
            )}
            {/* render mobile image if it exists */}
            {section.images?.[0]?.mobileImage?.gatsbyImageData && (
              <Hide above="tablet">
                <GatsbyImage
                  image={section.images[0].mobileImage.gatsbyImageData}
                  alt={section.images[0].mobileImageAltText ?? ''}
                />
              </Hide>
            )}
          </Box>
        </GridItem>
        <GridItem width={[1, null, 0.5]}>
          {section.metadata?.context?.eyebrow && (
            <Text
              color="viridian.base"
              paddingBottom={3}
              textAlign={['center', null, 'start']}
            >
              {section.metadata.context.eyebrow}
            </Text>
          )}

          {section.body?.raw && (
            <Box textAlign={['center', null, 'start']}>
              <RichTextWrapper contentfulRichText={section.body} />
            </Box>
          )}
          {accordion && (
            <Accordion allowToggle width="100%">
              {accordionSections.map((section, index) => {
                if (!section || !('contentful_id' in section)) {
                  return null;
                }

                // Return null if title doesn't exist
                if (!section.metadata?.context?.title) {
                  return null;
                }

                // If body doesn't exist but title does, render as list
                if (!section.body) {
                  return (
                    <Box
                      key={section.contentful_id}
                      borderBottom="1px solid"
                      borderColor="tints.lightGrey"
                      marginTop={index === 0 ? [8, null, 10] : []}
                      paddingBottom={[4, null, 6]}
                      marginBottom="6"
                    >
                      <Text fontWeight="medium">
                        {section.metadata?.context?.title}
                      </Text>
                    </Box>
                  );
                }

                return (
                  <AccordionItem key={section.contentful_id}>
                    <AccordionButton
                      sx={{ fontWeight: 'medium' }}
                      onClick={() =>
                        trackAccordionClick(
                          section.metadata?.context?.title || '',
                        )
                      }
                    >
                      {section.metadata?.context?.title ?? ''}
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel sx={{ py: 3 }}>
                      <Grid spacing={2}>
                        <GridItem width={1}>
                          <RichTextWrapper
                            contentfulRichText={section.body}
                            options={{
                              references:
                                section.body?.references?.filter(notEmpty) ??
                                [],
                            }}
                          />
                        </GridItem>

                        {section.subsections?.map((subsection) => {
                          if (!subsection || !('contentful_id' in subsection))
                            return null;

                          if (!subsection.body) return null;

                          return (
                            <GridItem
                              width={1 / 3}
                              key={subsection.contentful_id}
                            >
                              <RichTextWrapper
                                contentfulRichText={subsection.body}
                                options={{
                                  references:
                                    subsection.body?.references?.filter(
                                      notEmpty,
                                    ) ?? [],
                                  overrideOptions: {
                                    renderNode: {
                                      [BLOCKS.HEADING_6]: (_, children) => (
                                        <Text
                                          size="lg"
                                          marginBottom={3}
                                          fontWeight="medium"
                                        >
                                          {children}
                                        </Text>
                                      ),
                                      [BLOCKS.PARAGRAPH]: (_, children) => (
                                        <Text size="md" marginBottom={3}>
                                          {children}
                                        </Text>
                                      ),
                                    },
                                  },
                                }}
                              />
                            </GridItem>
                          );
                        })}
                      </Grid>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </GridItem>
      </Grid>
    </SectionContainer>
  );
};

export default DigitalResultsSection;
